// Copyright (c) 2021 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { ConnectorStatus, StopReason } from "model/connector";
import {
  ChargerConnectionType,
  IUserChargingControl,
  RfidAccessRights,
  eRfidLimitPeriod,
} from "shared/types/cloudtypes";
import { TariffAssociation } from "graphql/api";

export interface IOcppCharger {
  fk_charger: number; // index of t_charger DB table
  chargepoint: string;
  connectionType: ChargerConnectionType | undefined;
  // ocpp configuration data
  vendor?: string;
  model?: string;
  firmware?: string;
  serial?: string;
  numberofconnectors?: number;
  hasSmartCharging: boolean;
  // user configuration data
  name?: string;
  nominalPower?: number;
  restrictAuth: boolean;
  coordinates?: number[];
  tariffs: TariffAssociation[];
}

export interface IConnectorConfig {
  name: string | undefined;
  evseid: string | undefined;
  plugType: string | undefined;
  nominalPower: number | undefined;
  isDc: boolean | undefined;
  nrPhases: number | undefined;
}

export interface IConnectorData {
  // measurement data
  lastUpdate?: number;
  power?: number;
  current?: number[];
  voltage?: number[];
  sessionEnergy?: number;
  energymeter?: number;
  // status data
  status?: ConnectorStatus;
  errorCode?: string;
  errorInfo?: string;
  statusTimestamp?: number;
}

export interface IOcppChargingSession {
  id: number;
  fk_charger: number;
  fk_tariff: number | undefined;
  startTimestamp: number;
  stopTimestamp?: number | undefined;
  connector: number;
  rfid: string | undefined;
  rfidId: number | undefined;
  startMeter: number;
  stopMeter?: number | undefined;
  stopReason?: StopReason | undefined;
  costs?: number | undefined;
}

export interface IRfid {
  id: number;
  siteId: number;
  rfid: string;
  isActive: boolean;
  name: string | undefined;
  chargerRestricted: boolean;
  limitStart: number | undefined;
  limitEnd: number | undefined;
  limitEnergy: number | undefined;
  limitCost: number | undefined;
  limitPeriod: eRfidLimitPeriod | undefined;
  maxPowerAc: number | undefined;
  maxPowerDc: number | undefined;
  accessRights: RfidAccessRights;
  category: string | undefined;
  additionalData: string | undefined;
  carName: string | undefined;
  carBattery: number | undefined;
  // extended data, only available after extended request
  chargers?: number[];
  siteIds?: number[];
  tariffs?: TariffAssociation[];
  usedEnergy?: number;
  usedCost?: number;
  lastUsage?: number;
}

export interface IRefusedRfid {
  charger: number;
  rfid: string;
  firstAttempt: number;
  lastAttempt: number;
  counter: number;
}

export interface ITariff {
  id: number;
  siteId: number | undefined;
  name: string;
  currency: string;
  isActive: boolean | undefined;
  priority: number | undefined;
  costPerEnergy: number | undefined;
  costPerPeriod: number | undefined;
  validFrom: number | undefined;
  validUntil: number | undefined;
  timeStart: number | undefined;
  timeEnd: number | undefined;
}

export type IOcppChargingControl = Omit<IUserChargingControl, "useAtNewSession"> & {
  id: number; // change type of id from string to number
};

export interface IStateOcpp {
  charger: IOcppCharger[];
  lastOcppMessage: number[];
  connectorConfig: (IConnectorConfig | undefined)[][];
  connectorData: (IConnectorData | undefined)[][];

  chargingSession: IOcppChargingSession[];
  pendingChargingSession: IOcppChargingSession[];
  minSessionTimestamp: number | undefined;
  maxSessionTimestamp: number | undefined;
  chargingControl: IOcppChargingControl | undefined;

  rfid: IRfid[];
  siteTariff: ITariff[];
  sessionTariff?: ITariff[];
  refusedRfid?: IRefusedRfid[];
  // chargers of sites other than the currently selected one, loaded on demand to populate
  // the charger-restriction selection of rfid cards that are valid for several sites
  rfidSiteChargers?: { siteId: number; chargers: IOcppCharger[] }[];
}

/* initial state */
export const INITIAL_STATE: IStateOcpp = {
  charger: [],
  lastOcppMessage: [],
  connectorConfig: [],
  connectorData: [],
  chargingSession: [],
  pendingChargingSession: [],
  minSessionTimestamp: undefined,
  maxSessionTimestamp: undefined,
  chargingControl: undefined,
  rfid: [],
  siteTariff: [],
};
